import forEach from 'lodash/forEach'
import isString from 'lodash/isString'

import {ValidationError} from '../../exceptions'
import Height from '../../types/Height'
import {isFilledString} from '../../utils'

const validate = (result) => {
  if (isString(result)) throw new ValidationError(result)
  return result // returning a false here will short circuit the `forEach`
}
const generate = (tests) => (value) => forEach(tests, (test) => validate(test(value)))

// Helpers

// KEY:
// false  - pass this and all remaining tests
// String - error
// other  - pass

const pass = () => true
const ifNotThenReturn = (fn, backup) => (value) => fn(value) || backup

const isInput = (x) => isFilledString(x)
const isWhole = (x) => x === `${parseInt(x, 10)}`
const isHalf = (x) => x === `${parseFloat(x, 10)}` && parseFloat(x) % 1 === 0.5
const isWholeOrHalf = (x) => isWhole(x) || isHalf(x)

// Fails

const wholeNumber = ifNotThenReturn(isWhole, 'Please enter a whole number.')
const wholeOrHalfNumber = ifNotThenReturn(isWholeOrHalf, 'Please enter a whole or half number.')

const required = (message) => ifNotThenReturn(isInput, message)
const notRequired = ifNotThenReturn(isInput, false) // returning false will short circuit validation

const ltgt = (y, z, message) => ifNotThenReturn((x) => x >= y && x <= z, message)

// Exports

export const PASS = pass

const MIN_BIRTH_YEAR = 1900
const MAX_BIRTH_YEAR = new Date().getFullYear()

export const BIRTH_YEAR = generate([
  required('Please enter your birth year to continue.'),
  wholeNumber,
  ltgt(
    MIN_BIRTH_YEAR,
    MAX_BIRTH_YEAR,
    `Please enter a birth year between ${MIN_BIRTH_YEAR} and ${MAX_BIRTH_YEAR}.`,
  ),
])

const MIN_CHEST = 32
const MAX_CHEST = 68

export const CHEST = generate([
  notRequired,
  wholeNumber,
  ltgt(MIN_CHEST, MAX_CHEST, `Please enter a size between ${MIN_CHEST} and ${MAX_CHEST}, or skip.`),
])

const MIN_HEIGHT = 60
const MAX_HEIGHT = 83
const MIN_HEIGHT_FORMATTED = `${Height.toFeet(MIN_HEIGHT)}'${Height.toInches(MIN_HEIGHT)}"`
const MAX_HEIGHT_FORMATTED = `${Height.toFeet(MAX_HEIGHT)}'${Height.toInches(MAX_HEIGHT)}"`

export const HEIGHT = generate([
  required('Please enter your height to continue.'),
  wholeNumber,
  ltgt(
    MIN_HEIGHT,
    MAX_HEIGHT,
    `Please enter a height between ${MIN_HEIGHT_FORMATTED} and ${MAX_HEIGHT_FORMATTED}.`,
  ),
])

const MIN_INSEAM = 24
const MAX_INSEAM = 38

export const INSEAM = generate([
  notRequired,
  wholeNumber,
  ltgt(
    MIN_INSEAM,
    MAX_INSEAM,
    `Please enter a size between ${MIN_INSEAM} and ${MAX_INSEAM}, or skip.`,
  ),
])

const MIN_NECK = 13
const MAX_NECK = 25

export const NECK = generate([
  notRequired,
  wholeOrHalfNumber,
  ltgt(MIN_NECK, MAX_NECK, `Please enter a size between ${MIN_NECK} and ${MAX_NECK}, or skip.`),
])

const MIN_SHOE = 6
const MAX_SHOE = 17

export const SHOE = generate([
  required('Please enter your shoe size to continue.'),
  wholeOrHalfNumber,
  ltgt(MIN_SHOE, MAX_SHOE, `Please enter a size between ${MIN_SHOE} and ${MAX_SHOE}.`),
])

const MIN_SLEEVE = 30
const MAX_SLEEVE = 40

export const SLEEVE = generate([
  notRequired,
  wholeNumber,
  ltgt(
    MIN_SLEEVE,
    MAX_SLEEVE,
    `Please enter a size between ${MIN_SLEEVE} and ${MAX_SLEEVE}, or skip.`,
  ),
])

const MIN_WAIST = 26
const MAX_WAIST = 62

export const WAIST = generate([
  notRequired,
  wholeNumber,
  ltgt(MIN_WAIST, MAX_WAIST, `Please enter a size between ${MIN_WAIST} and ${MAX_WAIST}, or skip.`),
])

const MIN_WEIGHT = 100
const MAX_WEIGHT = 500

export const WEIGHT = generate([
  required('Please enter your weight to continue.'),
  wholeNumber,
  ltgt(MIN_WEIGHT, MAX_WEIGHT, `Please enter a weight between ${MIN_WEIGHT} and ${MAX_WEIGHT}.`),
])
